<template>
  <component 
    :is="iconComponent" 
    v-bind="iconProps" 
    :class="className" 
    :style="iconStyle"
  />
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";
import { LucideIcons } from "@/icons/lucide";
import { HeroIcons, DefaultHeroIcon } from "@/icons/hero";
import { Icon as IconifyIcon } from "@iconify/vue";
import { logger } from "@/utils/logger";

// 元件 Props 介面定義
interface Props {
  /** 圖示名稱，支援 kebab-case 和 snake_case */
  name: string;
  /** 圖示庫類型 */
  library?: "lucide" | "hero" | "iconify";
  /** CSS 類別名稱 */
  class?: string;
  /** 圖示尺寸（僅適用於部分圖示庫） */
  size?: string | number;
  /** 圖示顏色 */
  color?: string;
  /** 寬度（主要用於 Iconify） */
  width?: string | number;
  /** 高度（主要用於 Iconify） */
  height?: string | number;
}

// 預設 Props 值
const props = withDefaults(defineProps<Props>(), {
  library: "lucide",
});

// 獲取所有未定義的屬性
const attrs = useAttrs();

// 計算 CSS 類別名稱
const className = computed(() => {
  const baseClass = props.class || "";
  // 只有在非 Iconify 圖示庫時才使用 Tailwind 尺寸類別
  const sizeClass = props.size && props.library !== "iconify" ? `w-${props.size} h-${props.size}` : "";
  return [baseClass, sizeClass].filter(Boolean).join(" ");
});

// 計算內聯樣式
const iconStyle = computed(() => {
  const style: Record<string, string> = {};

  // color: all libraries
  if (props.color) {
    style.color = props.color;
  }

  // width/height for all when size is number
  if (props.size && typeof props.size === 'number') {
    style.width = `${props.size}px`;
    style.height = `${props.size}px`;
  }

  return style;
});

/**
 * 將 kebab-case 或 snake_case 字串轉換為 PascalCase
 * 例如：'check-circle' -> 'CheckCircle', 'user_profile' -> 'UserProfile'
 */
const toPascalCase = (str: string): string => {
  return str
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join("");
};

// 計算圖示元件屬性
const iconProps = computed(() => {
  const baseProps = { ...attrs };

  // 針對 Iconify 添加特殊屬性
  if (props.library === "iconify") {
    // 優先使用 width/height props，其次使用 size prop
    const iconWidth = props.width || props.size;
    const iconHeight = props.height || props.size;

    if (iconWidth) baseProps.width = iconWidth;
    if (iconHeight) baseProps.height = iconHeight;
    if (props.name) baseProps.icon = props.name;

    // 移除 class 屬性，因為 Iconify 需要特殊處理
    delete baseProps.class;
  }

  // Lucide & Heroicons: color prop 傳遞
  if (props.library !== "iconify" && props.color) {
    baseProps.color = props.color;
  }

  return baseProps;
});

// 計算要渲染的圖示元件
const iconComponent = computed(() => {
  const iconName = props.name;
  
  switch (props.library) {
    case "lucide": {
      const pascalCaseName = toPascalCase(iconName);
      const iconComponent = LucideIcons[pascalCaseName as keyof typeof LucideIcons];
      // 如果 LucideIcons 中沒有找到，使用 Lucide 的 HelpCircle 作為預設
      return iconComponent || LucideIcons.HelpCircle;
    }
    
    case "hero": {
      const pascalCaseName = toPascalCase(iconName);
      const iconComponent = HeroIcons[pascalCaseName as keyof typeof HeroIcons];
      return iconComponent || DefaultHeroIcon;
    }
    
    case "iconify": {
      return IconifyIcon;
    }
    
    default: {
      logger.warn('Unsupported icon library', { library: props.library });
      return DefaultHeroIcon;
    }
  }
});
</script>

<style scoped>
/* 預設圖示樣式 */
.icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}

/* 確保 SVG 圖示正確顯示，但不強制尺寸 */
svg {
  /* fill: currentColor; */
  display: block;
}
</style>
